import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  Calendar,
  CheckCircle2,
  TrendingUp,
  Users,
  Sparkles,
  Target,
  Compass,
  Search,
  MessageSquare,
  Flame,
  FileText,
  Megaphone,
  Star,
  Zap,
  Award,
  Eye,
  DollarSign,
  Briefcase,
  ShieldCheck,
  ChevronDown,
  Phone,
  Mail,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALENDLY_URL = "/book";

// ---- Editorial portrait imagery ----
const portraits = {
  hero1: "https://images.unsplash.com/photo-1594489005731-5f1e09b71d00?w=400&q=80&auto=format&fit=crop",
  hero2: "https://images.unsplash.com/photo-1613309561345-87a0798e1d06?w=400&q=80&auto=format&fit=crop",
  hero3: "https://images.unsplash.com/photo-1649433658557-54cf58577c68?w=400&q=80&auto=format&fit=crop",
  result1: "https://images.unsplash.com/photo-1598411435746-47bfecc3ea98?w=900&h=560&q=80&auto=format&fit=crop&crop=faces,entropy",
  result2: "https://plus.unsplash.com/premium_photo-1708275308965-bde27579612d?w=900&h=560&q=80&auto=format&fit=crop&crop=faces,entropy",
  result3: "/assets/fintech.jpg",
  result4: "https://images.unsplash.com/photo-1599928337729-09dac023dd52?w=900&h=560&q=80&auto=format&fit=crop&crop=faces,entropy",
  studio: "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=1200&q=80&auto=format&fit=crop",
};

// ---- Section: Sticky topbar ----
function TopBar({ scrollToForm }) {
  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-black/5">
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-14 md:h-16 flex items-center justify-between">
        <a href="/" className="flex items-center">
          <img src="/assets/logo_light.png" alt="BrandChilly" className="h-7 md:h-9 w-auto" />
        </a>
        <div className="flex items-center gap-2">
          <a href="tel:+918979106414" className="!hidden md:!inline-flex l-btn-ghost text-sm py-2.5 px-4">
            <Phone size={14} /> +91 89791 06414
          </a>
          <button onClick={scrollToForm} className="l-btn-primary text-xs md:text-sm py-2.5 px-4 md:px-5 whitespace-nowrap" data-testid="topbar-cta">
            <span className="hidden sm:inline">Get Free Roadmap</span>
            <span className="sm:hidden">Free Roadmap</span>
            <ArrowUpRight size={14} />
          </button>
        </div>
      </div>
    </header>
  );
}

// ---- Section: Hero with form ----
function Hero({ formRef, onSubmitted }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", niche: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.niche.trim()) {
      toast.error("Please fill in all the fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        niche: form.niche.trim(),
        message: "Landing page lead — requested 90-day personalised roadmap with execution",
        source: "landing_personal_branding",
      });
      if (window.bcTrack) window.bcTrack("Lead", { content_name: "PersonalBrandingLanding" });
      onSubmitted();
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="relative overflow-hidden">
      <div
        aria-hidden
        className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(217,4,41,0.08), transparent 70%)" }}
      />
      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-8 pb-14 md:pt-20 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="l-pill mb-5">
            <Flame size={13} className="l-red" /> Personal Branding · For Founders
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-[34px] sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight"
          >
            Become the founder<br />
            <span className="l-red">people search for.</span>
          </motion.h1>
          <p className="mt-5 md:mt-7 max-w-xl text-zinc-600 text-base md:text-lg leading-relaxed">
            Build a personal brand that compounds into real audience, authority and
            revenue. Same frameworks behind <strong className="text-black">0 → 3.54M YouTube</strong> and
            millions on Instagram.
          </p>

          <ul className="mt-6 md:mt-7 grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-w-xl">
            {[
              "Personalised 90-day roadmap with execution",
              "30·30·40 content framework",
              "Hooks, scripts & CTAs ready to film",
              "Direct 1:1 with Saurabh Khaspuri",
            ].map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-zinc-700">
                <CheckCircle2 size={16} className="l-red mt-0.5 flex-shrink-0" />
                {b}
              </li>
            ))}
          </ul>

          <div className="mt-7 md:mt-10 flex flex-wrap items-center gap-4">
            <div className="flex -space-x-3">
              {[portraits.hero1, portraits.hero2, portraits.hero3].map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  className="w-9 h-9 md:w-10 md:h-10 rounded-full object-cover border-2 border-white l-portrait"
                />
              ))}
            </div>
            <div className="text-sm text-zinc-600">
              <div className="flex items-center gap-1 text-amber-500">
                {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={13} fill="currentColor" />)}
              </div>
              <div className="text-[11px] md:text-xs text-zinc-500 mt-0.5">Trusted by founders growing 6–7 figure brands</div>
            </div>
          </div>
        </div>

        {/* Lead form */}
        <div ref={formRef} className="lg:col-span-5">
          <div className="l-card p-5 md:p-8 relative" style={{ boxShadow: "0 30px 80px -20px rgba(217,4,41,0.18)" }}>
            <div className="absolute -top-3 left-5 md:left-7 inline-flex items-center gap-2 bg-black text-white text-[10px] md:text-[11px] tracking-[0.2em] uppercase px-3 py-1.5 rounded-full">
              <Zap size={11} className="l-red" /> Limited founders / month
            </div>
            <h3 className="text-2xl md:text-3xl mt-2 md:mt-0 mb-1">Get your <span className="l-red">90-day roadmap</span> with execution</h3>
            <p className="text-sm text-zinc-600 mb-5">Fill below — we'll route you to book a free discovery call with Saurabh.</p>
            <form onSubmit={submit} className="space-y-3" data-testid="landing-form">
              <input type="text" required value={form.name} onChange={update("name")} placeholder="Full name *" className="l-input" data-testid="landing-name" />
              <input type="tel" required value={form.phone} onChange={update("phone")} placeholder="Phone / WhatsApp *" className="l-input" data-testid="landing-phone" />
              <input type="email" required value={form.email} onChange={update("email")} placeholder="Email *" className="l-input" data-testid="landing-email" />
              <input type="text" required value={form.niche} onChange={update("niche")} placeholder="Your niche (e.g. Fintech, Coaching) *" className="l-input" data-testid="landing-niche" />
              <button type="submit" disabled={submitting} className="l-btn-primary w-full text-sm md:text-base py-4 disabled:opacity-60" data-testid="landing-submit">
                {submitting ? "Sending…" : "Continue → Book Discovery Call"} {!submitting && <ArrowUpRight size={18} />}
              </button>
            </form>
            <div className="mt-4 flex items-center gap-2 text-[11px] md:text-xs text-zinc-500">
              <ShieldCheck size={13} className="l-red" /> Your details are private. Zero spam.
            </div>
          </div>
        </div>
      </div>

      {/* Trust strip / metric chips */}
      <div className="border-y border-black/5 bg-[var(--l-soft)] py-5 md:py-6 overflow-hidden">
        <div className="l-marquee-track whitespace-nowrap">
          {[
            "0 → 3.54M YouTube subscribers",
            "898K Instagram from zero",
            "458K Instagram from zero",
            "47K YouTube in 6 months",
            "37K YouTube in 6 months",
            "15+ years brand building",
          ].concat([
            "0 → 3.54M YouTube subscribers",
            "898K Instagram from zero",
            "458K Instagram from zero",
            "47K YouTube in 6 months",
            "37K YouTube in 6 months",
            "15+ years brand building",
          ]).map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-[11px] md:text-[13px] tracking-wider uppercase text-zinc-600">
              <Flame size={13} className="l-red" />
              <span className="font-medium">{s}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---- Section: Pain points ----
function PainPoints() {
  const pains = [
    { icon: Eye, title: "You're invisible online", desc: "You know your craft. Nobody knows you. Every great client is going to a louder competitor with half your skill." },
    { icon: MessageSquare, title: "Your content gets ignored", desc: "Three months of posting. 12 likes per post. You've blamed the algorithm — but the truth is the strategy was never built for you." },
    { icon: TrendingUp, title: "You're trading time for money", desc: "Every project is a fresh hunt. No inbound. No audience compounding. The minute you stop selling, the income stops." },
    { icon: Target, title: "No system, no momentum", desc: "Random reels, random posts, random results. You don't have a brand — you have a content graveyard." },
  ];
  return (
    <section className="py-14 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
          <div className="l-eyebrow mb-4">If any of this hits home…</div>
          <h2 className="text-3xl sm:text-4xl md:text-6xl leading-[1.05]">
            You're not bad at content.<br /><span className="l-red italic">You just don't have the system.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {pains.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} className="l-card p-6 md:p-7">
                <div className="w-11 h-11 rounded-full bg-red-50 flex items-center justify-center mb-4 md:mb-5">
                  <Icon size={18} className="l-red" />
                </div>
                <h3 className="text-lg md:text-xl mb-2">{p.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{p.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---- Section: Why now ----
function WhyNow() {
  const items = [
    { stat: "73%", desc: "of B2B buyers research the founder on Instagram or LinkedIn before making a buying decision." },
    { stat: "₹2 Cr+", desc: "average yearly inbound revenue for founders with a 100K+ niche-relevant audience." },
    { stat: "4.5x", desc: "lower cost of customer acquisition for founder-led brands vs. paid ads alone." },
    { stat: "10x", desc: "creator economy boom expected in India over the next 5 years (Goldman Sachs, 2024)." },
  ];
  return (
    <section className="py-14 md:py-28 bg-[var(--l-soft)]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="l-eyebrow mb-4">Why now · the boom is here</div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl leading-[1.05]">
              In 5 years, every category will be <span className="l-red italic">won by a face</span>, not a logo.
            </h2>
            <p className="mt-4 md:mt-5 text-zinc-600 leading-relaxed text-sm md:text-base">
              India just crossed 700M smartphone users. Trust is shifting from brands to people. AI is
              commoditising content — but personal brand is the one thing AI can't fake. The founders
              who lock in audience now will own the next decade.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-3 md:gap-4">
            {items.map((it, i) => (
              <div key={i} className="l-card p-5 md:p-6">
                <div className="text-3xl md:text-5xl font-medium" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                  <span className="l-red">{it.stat}</span>
                </div>
                <p className="mt-2 md:mt-3 text-xs md:text-sm text-zinc-600 leading-relaxed">{it.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---- Section: Benefits ----
function Benefits() {
  const benefits = [
    { icon: TrendingUp, title: "Inbound on autopilot", desc: "Wake up to DMs from clients, partners and investors who already trust you." },
    { icon: DollarSign, title: "Premium pricing power", desc: "Founders with audience charge 3–10x more than commodity providers in the same niche." },
    { icon: Briefcase, title: "Hiring magnet", desc: "A-players want to work with founders they've watched build something. Cuts hiring costs by half." },
    { icon: Award, title: "Investor / partner trust", desc: "A strong founder presence shortens fundraising and closes warm intros." },
    { icon: Users, title: "Audience compounding", desc: "Every follower today is a free distribution channel for every product you launch later." },
    { icon: ShieldCheck, title: "Recession-proof moat", desc: "Algorithms change, ads get pricier — your owned audience is the one asset that compounds." },
  ];
  return (
    <section className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-3xl mb-14">
          <div className="l-eyebrow mb-4">The compounding asset</div>
          <h2 className="text-4xl md:text-6xl leading-[1.05]">
            What you actually get when you build a <span className="l-gold italic">real</span> personal brand.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {benefits.map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={i} className="l-card p-7">
                <Icon size={22} className="l-red mb-4" />
                <h3 className="text-xl mb-2">{b.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{b.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---- Section: Results showcase ----
function Results() {
  const cases = [
    { img: portraits.result1, niche: "Education / EdTech", metric: "0 → 3.54M", platform: "YouTube subscribers", note: "From silent expert to category authority in under 24 months." },
    { img: portraits.result2, niche: "Lifestyle / Coaching", metric: "0 → 898K", platform: "Instagram followers", note: "Sold-out coaching cohorts and ₹1Cr+ ARR off audience alone." },
    { img: portraits.result3, niche: "Fintech / Investing", metric: "0 → 458K", platform: "Instagram followers", note: "Inbound investors and clients within 8 months of launch." },
    { img: portraits.result4, niche: "Career / Tech", metric: "0 → 47K", platform: "YouTube · 6 months", note: "Featured speaker offers, brand deals, recruiting pipeline." },
  ];
  return (
    <section className="py-14 md:py-28 bg-[var(--l-soft)]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
          <div className="l-eyebrow mb-4">Receipts, not promises</div>
          <h2 className="text-3xl sm:text-4xl md:text-6xl leading-[1.05]">
            Real founders. <span className="l-red italic">Real numbers.</span>
          </h2>
          <p className="mt-4 text-sm md:text-base text-zinc-600">
            Identities anonymised at the request of our partners — but the growth is ours.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
          {cases.map((c, i) => (
            <div key={i} className="l-card overflow-hidden group">
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={c.img}
                  alt={c.niche}
                  className="w-full h-full object-cover l-portrait group-hover:scale-105 transition-transform duration-700"
                  style={{ objectPosition: "center top" }}
                />
              </div>
              <div className="p-5 md:p-7">
                <div className="text-[11px] tracking-[0.22em] uppercase text-zinc-500">{c.niche}</div>
                <div className="mt-2 md:mt-3 flex items-baseline gap-3">
                  <div className="text-4xl md:text-6xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                    <span className="l-red">{c.metric}</span>
                  </div>
                </div>
                <div className="text-sm text-zinc-600 mt-1">{c.platform}</div>
                <p className="mt-3 md:mt-4 text-sm md:text-base text-zinc-700 leading-relaxed">{c.note}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---- Section: Roadmap (30·30·40) ----
function Roadmap() {
  const pillars = [
    { pct: "30", title: "Authority", desc: "Frameworks, breakdowns and opinions only you can write — proof you know your craft." },
    { pct: "30", title: "Relatability", desc: "Founder stories, BTS, real moments — the human layer that turns viewers into believers." },
    { pct: "40", title: "Virality", desc: "Hook-first, algorithm-engineered content — where reach actually compounds." },
  ];
  return (
    <section className="py-14 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
          <div className="l-eyebrow mb-4">Our signature framework</div>
          <h2 className="text-3xl sm:text-4xl md:text-6xl leading-[1.05]">
            The <span className="l-red italic">30·30·40</span> framework that builds unkillable brands.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10 md:mb-14">
          {pillars.map((p, i) => (
            <div key={i} className="l-card l-card-cream p-7 md:p-8 text-center">
              <div className="text-6xl md:text-8xl font-medium leading-none" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                <span className="l-red">{p.pct}</span><span className="text-black">%</span>
              </div>
              <h3 className="text-xl md:text-2xl mt-4">{p.title}</h3>
              <p className="mt-3 text-sm text-zinc-600 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---- Section: 8-step process ----
function Process() {
  const steps = [
    { n: "01", icon: Search, t: "Niche research", d: "Map your category, audience and white space." },
    { n: "02", icon: Sparkles, t: "Top creators", d: "Decode who's already winning and why." },
    { n: "03", icon: Target, t: "What's working", d: "Hooks, formats, narratives that perform." },
    { n: "04", icon: Compass, t: "Your expertise", d: "Extract your sharpest, ownable insights." },
    { n: "05", icon: MessageSquare, t: "Common topics", d: "Where your voice meets audience demand." },
    { n: "06", icon: Flame, t: "Hooks", d: "Scroll-stoppers tested for your tone." },
    { n: "07", icon: FileText, t: "Scripts", d: "Frame-by-frame scripts ready to film." },
    { n: "08", icon: Megaphone, t: "CTAs", d: "Conversions, not just views." },
  ];
  return (
    <section className="py-20 md:py-28 bg-[var(--l-soft)]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-3xl mb-14">
          <div className="l-eyebrow mb-4">How we work · 8 steps</div>
          <h2 className="text-4xl md:text-6xl leading-[1.05]">
            An <span className="l-gold italic">assembly line</span> for authority.
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="l-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-3xl font-light" style={{ fontFamily: "Cormorant Garamond, serif", color: "#cbcbd1" }}>{s.n}</span>
                  <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center">
                    <Icon size={15} className="l-red" />
                  </div>
                </div>
                <div className="text-lg font-medium mb-1">{s.t}</div>
                <p className="text-sm text-zinc-600 leading-relaxed">{s.d}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---- Section: The Offer ----
function Offer({ scrollToForm }) {
  const includes = [
    "Personalised 90-day content roadmap with execution, custom to your niche",
    "Complete 30·30·40 framework set-up for your brand voice",
    "Niche & competitor intelligence breakdown",
    "Monthly content planner with hooks + scripts",
    "Bi-weekly 1:1 strategy calls with Saurabh Khaspuri",
    "WhatsApp / Slack support across the engagement",
    "Frame-by-frame Reels & Shorts scripts ready to film",
    "Performance review & creative iteration every 2 weeks",
  ];
  return (
    <section className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="l-eyebrow mb-4">The offer</div>
            <h2 className="text-4xl md:text-5xl leading-[1.05]">
              Get a personalised <span className="l-red italic">90-day roadmap with execution</span> to build a genuine audience and grow your brand faster.
            </h2>
            <p className="mt-5 text-zinc-600 leading-relaxed">
              In the first 30 days you'll have a strategy, a framework and a content engine ready to
              run. By day 90 you'll be the founder your category recognises.
            </p>
            <button onClick={scrollToForm} className="l-btn-primary mt-8" data-testid="offer-cta">
              Claim my roadmap <ArrowUpRight size={18} />
            </button>
          </div>
          <div className="lg:col-span-7">
            <div className="l-card l-card-cream p-8 md:p-10">
              <div className="text-[11px] tracking-[0.22em] uppercase text-zinc-500 mb-3">What's included</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {includes.map((it, i) => (
                  <li key={i} className="flex items-start gap-3 text-zinc-800 text-sm">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 size={13} className="l-red" />
                    </span>
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---- Section: Pricing ----
function Pricing({ scrollToForm }) {
  return (
    <section className="py-14 md:py-28 bg-[var(--l-soft)]">
      <div className="max-w-5xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-12">
          <div className="l-eyebrow mb-4">The investment</div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl leading-[1.05]">
            One signature engagement. <span className="l-red italic">90 days to a brand of heat.</span>
          </h2>
        </div>

        <div className="l-card relative overflow-hidden p-6 md:p-14" style={{
          background: "linear-gradient(180deg, #ffffff 0%, #fff8f3 100%)",
          boxShadow: "0 30px 80px -20px rgba(217,4,41,0.18)",
        }}>
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(217,4,41,0.10), transparent 70%)" }} />
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-center">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 bg-black text-white px-3 py-1.5 rounded-full text-[10px] md:text-[11px] tracking-[0.2em] uppercase mb-4 md:mb-5">
                <Star size={11} className="l-gold" /> Limited slots / month
              </div>
              <h3 className="text-2xl md:text-4xl">Signature 90-day engagement</h3>
              <p className="text-sm md:text-base text-zinc-600 mt-3 max-w-md">
                Done-with-you brand engine combining strategy, content and direct access to Saurabh.
                Every founder gets the same heat-tested framework.
              </p>
              <div className="mt-5 md:mt-7 flex items-center gap-3">
                <div className="text-5xl md:text-7xl font-medium" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                  ₹50,000
                </div>
                <div>
                  <div className="text-zinc-500 text-sm">+ GST</div>
                  <div className="text-zinc-400 text-xs">3 months total</div>
                </div>
              </div>
            </div>

            <div className="md:col-span-5">
              <button onClick={scrollToForm} className="l-btn-primary w-full text-sm md:text-base py-4" data-testid="pricing-cta">
                Apply for a slot <ArrowUpRight size={18} />
              </button>
              <a
                href={CALENDLY_URL}
                onClick={() => { try { if (window.fbq) window.fbq("track", "Lead", { content_name: "PersonalBrandingLanding", source: "pricing_book_call" }); if (window.gtag) window.gtag("event", "generate_lead", { value: 1, currency: "INR", source: "pricing_book_call" }); } catch(e){} }}
                className="l-btn-ghost w-full text-sm md:text-base py-4 mt-3"
                data-testid="pricing-calendly"
              >
                <Calendar size={16} /> Book a free call
              </a>
              <div className="mt-4 text-[11px] md:text-xs text-zinc-500 text-center">
                No-obligation 30-min discovery call · 100% strategy
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---- Section: FAQ ----
const FAQS = [
  {
    q: "I don't want to be on camera. Can we still build a personal brand?",
    a: "Yes. We design hybrid systems — voiceover Reels, text-led carousels, ghostwritten LinkedIn posts and behind-the-scenes formats — for founders who'd rather lead from the back. You'll own the brand without becoming an influencer.",
  },
  {
    q: "How is this different from hiring a content writer or social media agency?",
    a: "Most agencies sell content. We build a brand operating system. You get a strategy framework (30·30·40), niche research, hooks, scripts and CTAs designed for one specific person — you. It's the difference between a typist and a co-founder.",
  },
  {
    q: "When will I see real results?",
    a: "First 30 days: framework and content engine live. Days 30–60: first viral hits and inbound DMs. Days 60–90: predictable audience growth, qualified leads, premium positioning. We don't promise overnight virality — we engineer compounding.",
  },
  {
    q: "What if my niche is boring, technical, or B2B?",
    a: "Our best results are in B2B and technical niches — fintech, education, SaaS, coaching, healthcare. The narrower your niche, the easier it is to dominate. We've turned tax consultants and chip designers into 6-figure-audience founders.",
  },
  {
    q: "Do I need to post every day to build a personal brand?",
    a: "No. Quality over volume. Our framework recommends 3–5 high-leverage posts per week, designed to perform — not to fill a calendar. Algorithms reward signal, not noise.",
  },
  {
    q: "Which platforms do you focus on?",
    a: "Primary: Instagram and YouTube — where reach actually compounds. Secondary: LinkedIn for B2B and Twitter/X for thought leadership. We pick based on your audience and goals, not what's trending.",
  },
  {
    q: "Do you guarantee viral results or follower numbers?",
    a: "We guarantee process, not outcome. No honest agency can guarantee a specific follower count — but we guarantee you'll have a tested framework, weekly content output and the strategic clarity to keep growing long after our engagement ends.",
  },
  {
    q: "What's the commitment? Can I cancel after a month?",
    a: "Our signature engagement is a 3-month commitment because that's the minimum window to install the framework and see momentum. After 90 days, we offer optional month-to-month retainers for founders who want continued support.",
  },
];

function FAQItem({ q, a, isOpen, onToggle }) {
  return (
    <div className="l-card overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-4 p-6 text-left"
      >
        <span className="font-medium text-base md:text-lg pr-4">{q}</span>
        <ChevronDown
          size={20}
          className={`flex-shrink-0 mt-1 transition-transform duration-300 l-red ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`grid transition-all duration-500 ease-out ${
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <p className="px-6 pb-6 text-zinc-600 leading-relaxed">{a}</p>
        </div>
      </div>
    </div>
  );
}

function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section className="py-14 md:py-28">
      <div className="max-w-4xl mx-auto px-5 md:px-8">
        <div className="text-center mb-10 md:mb-12">
          <div className="l-eyebrow mb-4">Common questions</div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl leading-[1.05]">
            Everything you're probably <span className="l-red italic">wondering</span>.
          </h2>
        </div>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <FAQItem key={i} q={f.q} a={f.a} isOpen={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ---- Section: Final CTA ----
function FinalCTA({ submitted, scrollToForm }) {
  return (
    <section className="py-16 md:py-28 bg-black text-white">
      <div className="max-w-5xl mx-auto px-5 md:px-8 text-center">
        <div className="text-[11px] tracking-[0.28em] uppercase l-red mb-4 md:mb-5">Don't post another random reel</div>
        <h2 className="text-3xl sm:text-4xl md:text-6xl leading-[1.05] !text-white" style={{ fontFamily: "Cormorant Garamond, serif", color: "#ffffff" }}>
          Build the <span className="l-gold italic">one asset</span> nobody can take from you.<br />
          <span className="text-zinc-400">Your audience.</span>
        </h2>
        <p className="mt-5 md:mt-6 text-sm md:text-base text-zinc-400 max-w-xl mx-auto">
          Limited slots open every month. Book your free discovery call now.
        </p>
        <div className="mt-8 md:mt-9 flex flex-wrap justify-center gap-3">
          {submitted ? (
            <a href={CALENDLY_URL} onClick={() => { try { if (window.fbq) window.fbq("track", "Lead", { content_name: "PersonalBrandingLanding", source: "final_book_call" }); if (window.gtag) window.gtag("event", "generate_lead", { value: 1, currency: "INR", source: "final_book_call" }); } catch(e){} }} className="l-btn-primary text-sm md:text-base py-4 px-7 md:px-8" data-testid="final-cta-calendly">
              <Calendar size={18} /> Book Discovery Call
            </a>
          ) : (
            <button onClick={scrollToForm} className="l-btn-primary text-sm md:text-base py-4 px-7 md:px-8" data-testid="final-cta-form">
              Get my 90-day roadmap <ArrowUpRight size={18} />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

// ---- Footer mini ----
function FooterMini() {
  return (
    <footer className="py-8 md:py-10 bg-white border-t border-black/5">
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-zinc-500">
        <div className="flex items-center gap-3">
          <a href="/" className="flex items-center">
            <img src="/assets/logo_light.png" alt="BrandChilly" className="h-7 w-auto" />
          </a>
          <span>© {new Date().getFullYear()} · All rights reserved</span>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          <a href="mailto:brandchilly@gmail.com" className="flex items-center gap-1 hover:text-black transition">
            <Mail size={13} /> brandchilly@gmail.com
          </a>
          <a href="tel:+918979106414" className="flex items-center gap-1 hover:text-black transition">
            <Phone size={13} /> +91 89791 06414
          </a>
        </div>
      </div>
    </footer>
  );
}

// ---- Page ----
export default function LandingPage() {
  const formRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  // Meta Pixel + GA: fire ViewContent when landing page loads (for retargeting audiences)
  useEffect(() => {
    try {
      if (window.fbq) window.fbq("track", "ViewContent", { content_name: "PersonalBrandingLanding", content_category: "Lead Gen Landing" });
      if (window.gtag) window.gtag("event", "view_content", { content_name: "PersonalBrandingLanding" });
    } catch (e) {
      // swallow
    }
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const onSubmitted = () => {
    setSubmitted(true);
    navigate("/thankyou");
  };

  return (
    <div className="landing-light">
      <TopBar scrollToForm={scrollToForm} />
      <Hero formRef={formRef} onSubmitted={onSubmitted} />
      <PainPoints />
      <WhyNow />
      <Benefits />
      <Results />
      <Roadmap />
      <Process />
      <Offer scrollToForm={scrollToForm} />
      <Pricing scrollToForm={scrollToForm} />
      <FAQ />
      <FinalCTA submitted={submitted} scrollToForm={scrollToForm} />
      <FooterMini />
    </div>
  );
}
